import { c as _c } from "react/compiler-runtime";
import { Box, Stack, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/form/baseInputs/Button';
import { AlertTriangleIcon } from '@/components/icons/AlertTriangleIcon';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { ActionCard } from '@/components/layout/ActionCard/ActionCard';
import { SplitScreenLayout } from '@/components/layout/SplitScreenLayout/SplitScreenLayout';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { DocumentLoader, DocumentLoaderProvider } from '@/components/progress/DocumentLoader';
import { InsetCardChrome } from '@/modules/documents/components/HouseholdDocumentsViewer/HouseholdDocumentsViewer';
import { FullScreenModalHouseholdChatHost } from '@/modules/household/chat/components/FullScreenModalHouseholdChatHost';
import { HouseholdChatViewedContext } from '@/modules/household/chat/hooks/useHouseholdChatViewedContext';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { COLORS } from '@/styles/tokens/colors';
import { AnswerReviewHeader } from './AnswerReviewHeader';
import { AnswerReviewPanel } from './AnswerReviewPanel';
import type { AnswerReviewWorkbenchProps } from './AnswerReviewWorkbench.types';
import { FormDocumentLoader } from './FormDocumentLoader';
function CenteredState(t0) {
  const $ = _c(10);
  const {
    icon,
    title,
    message,
    role,
    action
  } = t0;
  const t1 = role === "status" ? "polite" : undefined;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      maxWidth: 400
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== action || $[2] !== icon || $[3] !== message || $[4] !== title) {
    t3 = <ActionCard variant="noCard" icon={icon} heading={title} description={message} actions={action} sx={t2} />;
    $[1] = action;
    $[2] = icon;
    $[3] = message;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== role || $[7] !== t1 || $[8] !== t3) {
    t4 = <Stack role={role} aria-live={t1} height="100%" alignItems="center" justifyContent="center" textAlign="center" p={3}>{t3}</Stack>;
    $[6] = role;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function ReviewContent(props) {
  const $ = _c(19);
  const {
    state,
    onRetryLoad
  } = props;
  if (state.kind === "loading" || state.kind === "extracting") {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <Box aria-hidden="true"><DocumentLoader indeterminate={true} /></Box>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    const t1 = state.kind === "loading" ? "Loading answer review" : "Generating answers";
    const t2 = state.kind === "loading" ? "Loading the review details\u2026" : state.message;
    let t3;
    if ($[1] !== t2) {
      t3 = <Box component="span" display="block" minHeight={40}>{t2}</Box>;
      $[1] = t2;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== t1 || $[4] !== t3) {
      t4 = <CenteredState role="status" icon={t0} title={t1} message={t3} />;
      $[3] = t1;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  if (state.kind === "error") {
    let t0;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <AlertTriangleIcon size={32} color={COLORS.FUNCTIONAL.ERROR.DEFAULT} />;
      $[6] = t0;
    } else {
      t0 = $[6];
    }
    let t1;
    if ($[7] !== onRetryLoad) {
      t1 = onRetryLoad ? <Button variant="primary" size="sm" onClick={onRetryLoad}>Try again</Button> : undefined;
      $[7] = onRetryLoad;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    let t2;
    if ($[9] !== state.message || $[10] !== t1) {
      t2 = <CenteredState role="alert" icon={t0} title="Answer review could not load" message={state.message} action={t1} />;
      $[9] = state.message;
      $[10] = t1;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    return t2;
  }
  if (state.kind === "unsupported") {
    let t0;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <FileSearch02Icon size={32} />;
      $[12] = t0;
    } else {
      t0 = $[12];
    }
    let t1;
    if ($[13] !== state.message) {
      t1 = <CenteredState icon={t0} title="This form cannot be reviewed" message={state.message} />;
      $[13] = state.message;
      $[14] = t1;
    } else {
      t1 = $[14];
    }
    return t1;
  }
  if (state.review.sections.length === 0) {
    let t0;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <CenteredState title="No questions to review" message="This form does not contain any active questions." />;
      $[15] = t0;
    } else {
      t0 = $[15];
    }
    return t0;
  }
  let t0;
  if ($[16] !== props || $[17] !== state.review) {
    t0 = <AnswerReviewPanel review={state.review} {...props} />;
    $[16] = props;
    $[17] = state.review;
    $[18] = t0;
  } else {
    t0 = $[18];
  }
  return t0;
}
function DocumentContent(t0) {
  const $ = _c(6);
  const {
    documentPane,
    state,
    activeSourceDocument
  } = t0;
  let content = documentPane;
  if (!content && state.kind === "loading") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <InsetCardChrome shouldCenterContent={true}><FormDocumentLoader /></InsetCardChrome>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    content = t1;
  }
  const t1 = activeSourceDocument?.id;
  let t2;
  if ($[1] !== content) {
    t2 = content ?? <CenteredState icon={<FileSearch02Icon size={32} />} title="Source document unavailable" message="The answer review can still be read without the source document." />;
    $[1] = content;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <Box component="section" aria-label="Source document" height="100%" overflow="auto" bgcolor={COLORS.GRAY[50]}><DocumentLoaderProvider documentId={t1}>{t2}</DocumentLoaderProvider></Box>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function AnswerReviewWorkbench(props) {
  const $ = _c(19);
  const theme = useTheme();
  let t0;
  if ($[0] !== theme.breakpoints) {
    t0 = theme.breakpoints.down("lg");
    $[0] = theme.breakpoints;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isNarrow = useMediaQuery(t0);
  const document = props.activeSourceDocument;
  let t1;
  if ($[2] !== document) {
    t1 = document ? {
      key: `document:${document.id}`,
      objectId: document.id,
      item: {
        type: "document",
        name: document.name
      }
    } : null;
    $[2] = document;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const viewedContext = t1;
  const [activePane, setActivePane] = useState("review");
  let t2;
  let t3;
  if ($[4] !== props.citationTargetFieldId) {
    t2 = () => {
      setActivePane(props.citationTargetFieldId ? "document" : "review");
    };
    t3 = [props.citationTargetFieldId];
    $[4] = props.citationTargetFieldId;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== activePane || $[8] !== isNarrow || $[9] !== props) {
    t4 = householdChatPane => !isNarrow ? <SplitScreenLayout resizable={true} rightPane={householdChatPane} header={<AnswerReviewHeader {...props} />} leftContent={<DocumentContent {...props} />} rightContent={<ReviewContent {...props} />} footer={null} /> : <Stack height="100%" width="100%" overflow="hidden"><AnswerReviewHeader {...props} /><Tabs value={activePane} onChange={(_event, value) => setActivePane(value)} aria-label="Answer review panes" variant="fullWidth" sx={{
        borderBottom: `1px solid ${COLORS.GRAY[200]}`
      }}><Tab id="document-tab" aria-controls="document-panel" value="document" label="Document" /><Tab id="review-tab" aria-controls="review-panel" value="review" label="Answers" /></Tabs><Box flex={1} minHeight={0}><Box id="document-panel" role="tabpanel" aria-labelledby="document-tab" height="100%" hidden={activePane !== "document"}><DocumentContent {...props} /></Box><Box id="review-panel" role="tabpanel" aria-labelledby="review-tab" height="100%" hidden={activePane !== "review"}><ReviewContent {...props} /></Box></Box>{householdChatPane}</Stack>;
    $[7] = activePane;
    $[8] = isNarrow;
    $[9] = props;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const content = t4;
  let t5;
  if ($[11] !== content) {
    t5 = t6 => {
      const {
        householdChatPane: householdChatPane_0
      } = t6;
      return content(householdChatPane_0);
    };
    $[11] = content;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5 || $[14] !== viewedContext) {
    t6 = <FullScreenModalHouseholdChatHost viewedContext={viewedContext} responsiveInModal={true} entryPointBottomOffset={16}>{t5}</FullScreenModalHouseholdChatHost>;
    $[13] = t5;
    $[14] = viewedContext;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== props.onClose || $[17] !== t6) {
    t7 = <FullScreenModal trowserKey={TrowserKey.Standalone} isOpen={true} onClose={props.onClose}>{t6}</FullScreenModal>;
    $[16] = props.onClose;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}